import React, { useState } from "react";
import { Link } from "react-router-dom";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Divider from "@mui/material/Divider";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import utils from "../../universal/utils";
import {
  DELETE_CONFIRMATION_TEXT,
  canSubmitDeletionRequest,
} from "./accountDataDeletionUtils";

const basePath = utils.getEdgeBasePath();
const deletionHeadingId = "account-data-deletion-heading";
const deletionToggleId = "account-data-deletion-toggle";
const deletionRegionId = "account-data-deletion-region";

export default function AccountDataDeletionSection({ user, defaultExpanded = false }) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [deleted, setDeleted] = useState(false);
  const [expanded, setExpanded] = useState(defaultExpanded);
  const [form, setForm] = useState({
    confirmationText: "",
    twoFactorCode: "",
  });

  const handleFormChange = (event) => {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const response = await fetch(`${basePath}/api/v1/data-deletion-request`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          confirmationText: form.confirmationText,
          twoFactorCode: form.twoFactorCode.trim(),
        }),
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data?.message || "Failed to delete account");
      }

      setDialogOpen(false);
      setDeleted(true);
      setForm({ confirmationText: "", twoFactorCode: "" });
      window.setTimeout(() => {
        window.location.assign("/");
      }, 1500);
    } catch (submitError) {
      setError(submitError.message || "Failed to delete account");
    } finally {
      setSubmitting(false);
    }
  };

  const canSubmit = canSubmitDeletionRequest({
    confirmationText: form.confirmationText,
    twoFactorEnabled: !!user?.twoFactorEnabled,
    twoFactorCode: form.twoFactorCode,
  });

  return (
    <Box sx={{ mt: 4 }}>
      <Divider sx={{ mb: 3 }} />
      <Box
        sx={{
          border: "1px solid rgba(244, 67, 54, 0.35)",
          backgroundColor: "rgba(244, 67, 54, 0.08)",
          borderRadius: 2,
          p: 3,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            flexWrap: "wrap",
            mb: expanded ? 1.5 : 0,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <WarningAmberIcon color="error" />
            <Typography id={deletionHeadingId} variant="h6" sx={{ fontWeight: 700 }}>
              Danger Zone
            </Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
            <Button
              id={deletionToggleId}
              size="small"
              color="error"
              onClick={() => setExpanded((prev) => !prev)}
              aria-expanded={expanded}
              aria-controls={deletionRegionId}
              sx={{ textTransform: "none", fontWeight: 700, alignSelf: "center" }}
            >
              {expanded ? "Hide danger zone" : "Show danger zone"}
            </Button>
          </Box>
        </Box>

        <Box
          id={deletionRegionId}
          role="region"
          aria-labelledby={deletionHeadingId}
          hidden={!expanded}
        >
          {expanded && (
            <Box>
            <Typography variant="body2" sx={{ color: "text.secondary", lineHeight: 1.6, mb: 2 }}>
              Permanently close your account and delete your personal data. Once confirmed, this action is final and your account cannot be recovered. Some history may be retained only in anonymized form where legally or operationally required.
            </Typography>

            {deleted && (
              <Alert severity="success" sx={{ mb: 2 }}>
                Your account has been permanently deleted. We sent a confirmation email and will redirect you shortly.
              </Alert>
            )}

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            <Box sx={{ mb: 2 }}>
              <Typography
                component={Link}
                to="/data-protection-policy-and-gdpr"
                sx={{ color: "primary.light", textDecoration: "none", display: "inline-block" }}
              >
                Review our GDPR policy
              </Typography>
            </Box>

            <Box>
              <Button
                variant="outlined"
                color="error"
                disabled={deleted}
                onClick={() => setDialogOpen(true)}
                sx={{ textTransform: "none", fontWeight: 700 }}
              >
                Close My Account Permanently
              </Button>
            </Box>
            </Box>
          )}
        </Box>

        <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth="sm">
          <DialogTitle>Close account permanently?</DialogTitle>
          <DialogContent>
            <Typography variant="body2" sx={{ mb: 2, color: "text.secondary", lineHeight: 1.6 }}>
              Are you sure you want to close your account? Once you confirm, your account will be deleted immediately and cannot be recovered. Ouro will only retain the minimal legally permitted records needed for audit, compliance, and misuse prevention.
            </Typography>

            <TextField
              fullWidth
              margin="dense"
              name="confirmationText"
              label={`Type "${DELETE_CONFIRMATION_TEXT}" to confirm`}
              value={form.confirmationText}
              onChange={handleFormChange}
            />

            {user?.twoFactorEnabled && (
              <TextField
                fullWidth
                margin="dense"
                name="twoFactorCode"
                label="Authenticator code"
                value={form.twoFactorCode}
                onChange={handleFormChange}
                slotProps={{ htmlInput: { maxLength: 6 } }}
                helperText="Your current 2FA code is required before your account can be deleted."
              />
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button
              variant="contained"
              color="error"
              disabled={!canSubmit || submitting}
              onClick={handleSubmit}
            >
              {submitting ? <CircularProgress size={20} color="inherit" /> : "Delete My Account"}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Box>
  );
}